<template>
  <!-- Header variant: registers as a Vuetify layout item so it stays pinned above the app bar -->
  <v-system-bar
    v-if="isVisible && !inline"
    order="-1"
    :height="barHeight"
    :style="barStyle"
    class="notice-bar justify-center"
  >
    <div ref="contentRef" class="notice-bar__content" v-html="noticeBar.html" @click="onContentClick"></div>
  </v-system-bar>

  <!-- Inline variant: plain block for use inside a page or dialog that covers the header -->
  <div v-else-if="isVisible" class="notice-bar" :style="barStyle">
    <div class="notice-bar__content" v-html="noticeBar.html" @click="onContentClick"></div>
  </div>
</template>

<script setup>
// import, props, emits, use
// Initial value only - replaced by the measured content height once mounted.
const DEFAULT_BAR_HEIGHT = 28

const props = defineProps({
  inline: {
    type: Boolean,
    default: false
  }
})

const appConfigStore = useAppConfigStore()
const appConfig = appConfigStore.appConfig
const noticeBar = appConfig?.web?.noticeBar

// data
const contentRef = ref(null)
const barHeight = ref(DEFAULT_BAR_HEIGHT)
let resizeObserver = null

// computed
const isVisible = computed(() => !!noticeBar?.isEnabled && !!noticeBar?.html)
const barStyle = computed(() => ({
  backgroundColor: noticeBar?.bgColor || '#FFF8E1',
  color: noticeBar?.textColor || 'inherit'
}))

onMounted(() => {
  if (props.inline || !contentRef.value) return

  // VSystemBar binds both its own height and the v-main layout offset to the `height` prop,
  // so the height has to be measured from the content or a wrapped notice would overlap the page.
  resizeObserver = new ResizeObserver(() => syncBarHeight())
  resizeObserver.observe(contentRef.value)
  syncBarHeight()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

// method
// The bar is sized to its content box so both variants end up exactly the same height.
const syncBarHeight = () => {
  if (!contentRef.value) return
  barHeight.value = Math.ceil(contentRef.value.scrollHeight)
}

// Keep in-app links inside the SPA instead of triggering a full page reload.
const onContentClick = (event) => {
  const anchor = event.target.closest('a')
  if (!anchor) return

  const href = anchor.getAttribute('href')
  if (!href || href.startsWith('#')) return
  if (anchor.target && anchor.target !== '_self') return
  if (/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href)) return

  event.preventDefault()
  navigateTo(href)
}
</script>

<style lang="scss" scoped>
.notice-bar {
  text-align: center;

  // Drop VSystemBar's own inline padding so both variants share one content box.
  // Compound with .v-system-bar to outrank Vuetify's own padding-inline-end rule.
  &.v-system-bar {
    padding-inline: 0;
  }

  // Every size/spacing rule lives here, so the header bar and the inline block render identically.
  &__content {
    font-size: 0.8125rem;
    font-weight: 400;
    letter-spacing: normal;
    line-height: 1.4;
    padding: 5px 8px;
    width: 100%;
  }

  :deep(a) {
    color: inherit;
    text-decoration: underline;
  }
}
</style>
